/* ============================================================
   UTENZO CART
   ============================================================ */

/**
 * Update all Utenzo header cart counters.
 *
 * Actual Utenzo markup:
 * <span class="ch-cart-count">0</span>
 */
function updateUtenzoCartCount(count) {
  const itemCount = Number(count);

  if (!Number.isFinite(itemCount)) return;

  document.querySelectorAll('.ch-cart-count').forEach((counter) => {
    counter.textContent = itemCount;
  });
}


/* ============================================================
   CART REMOVE BUTTON
   ============================================================ */

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems =
        this.closest('cart-items') ||
        this.closest('cart-drawer-items');

      if (!cartItems) return;

      cartItems.updateQuantity(
        this.dataset.index,
        0,
        event
      );
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    CartRemoveButton
  );
}


/* ============================================================
   CART ITEMS
   ============================================================ */

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById(
        'shopping-cart-line-item-status'
      ) ||
      document.getElementById(
        'CartDrawer-LineItemStatus'
      );

    const debouncedOnChange = debounce(
      (event) => {
        this.onChange(event);
      },
      ON_CHANGE_DEBOUNCE_TIMER
    );

    this.addEventListener(
      'change',
      debouncedOnChange.bind(this)
    );
  }


  /* ==========================================================
     PUB/SUB
     ========================================================== */

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        /*
         * If this CartItems instance generated the update,
         * don't reload itself again.
         */
        if (event.source === 'cart-items') {
          return;
        }

        return this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }


  /* ==========================================================
     QUANTITY VALIDATION
     ========================================================== */

  resetQuantityInput(id) {
    const input = this.querySelector(
      `#Quantity-${id}`
    );

    if (!input) return;

    input.value =
      input.getAttribute('value');

    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    if (!event || !event.target) return;

    event.target.setCustomValidity(message);
    event.target.reportValidity();

    this.resetQuantityInput(index);

    if (typeof event.target.select === 'function') {
      event.target.select();
    }
  }

  validateQuantity(event) {
    if (!event || !event.target) return;

    const inputValue =
      parseInt(event.target.value, 10);

    const index =
      event.target.dataset.index;

    let message = '';

    const min =
      parseInt(event.target.dataset.min, 10);

    const max =
      parseInt(event.target.max, 10);

    const step =
      parseInt(event.target.step, 10);

    if (
      !Number.isNaN(min) &&
      inputValue < min
    ) {
      message =
        window.quickOrderListStrings.min_error.replace(
          '[min]',
          event.target.dataset.min
        );
    } else if (
      !Number.isNaN(max) &&
      inputValue > max
    ) {
      message =
        window.quickOrderListStrings.max_error.replace(
          '[max]',
          event.target.max
        );
    } else if (
      !Number.isNaN(step) &&
      step > 0 &&
      inputValue % step !== 0
    ) {
      message =
        window.quickOrderListStrings.step_error.replace(
          '[step]',
          event.target.step
        );
    }

    if (message) {
      this.setValidity(
        event,
        index,
        message
      );

      return;
    }

    event.target.setCustomValidity('');
    event.target.reportValidity();

    const activeElementName =
      document.activeElement &&
      document.activeElement.getAttribute
        ? document.activeElement.getAttribute('name')
        : null;

    this.updateQuantity(
      index,
      inputValue,
      event,
      activeElementName,
      event.target.dataset.quantityVariantId
    );
  }

  onChange(event) {
    this.validateQuantity(event);
  }


  /* ==========================================================
     UPDATE CART WHEN AN EXTERNAL CART EVENT OCCURS
     ========================================================== */

  onCartUpdate() {
    /*
     * Cart drawer
     */
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(
        `${routes.cart_url}?section_id=cart-drawer`
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Cart drawer request failed: ${response.status}`
            );
          }

          return response.text();
        })

        .then((responseText) => {
          const html =
            new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

          const selectors = [
            'cart-drawer-items',
            '.cart-drawer__footer',
          ];

          selectors.forEach((selector) => {
            const targetElement =
              document.querySelector(selector);

            const sourceElement =
              html.querySelector(selector);

            if (
              targetElement &&
              sourceElement
            ) {
              targetElement.replaceWith(
                sourceElement
              );
            }
          });
        })

        .catch((error) => {
          console.error(
            'Unable to refresh cart drawer:',
            error
          );
        });
    }

    /*
     * Main cart page
     */
    return fetch(
      `${routes.cart_url}?section_id=main-cart-items`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Cart request failed: ${response.status}`
          );
        }

        return response.text();
      })

      .then((responseText) => {
        const html =
          new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

        const sourceQty =
          html.querySelector('cart-items');

        if (sourceQty) {
          this.innerHTML =
            sourceQty.innerHTML;
        }
      })

      .catch((error) => {
        console.error(
          'Unable to refresh cart:',
          error
        );
      });
  }


  /* ==========================================================
     SECTIONS TO RENDER
     ========================================================== */

  getSectionsToRender() {
    const sections = [];

    const mainCartItems =
      document.getElementById(
        'main-cart-items'
      );

    const mainCartFooter =
      document.getElementById(
        'main-cart-footer'
      );

    /*
     * Main cart items
     */
    if (
      mainCartItems &&
      mainCartItems.dataset.id
    ) {
      sections.push({
        id: 'main-cart-items',
        section:
          mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    /*
     * Live region
     */
    if (
      document.getElementById(
        'cart-live-region-text'
      )
    ) {
      sections.push({
        id: 'cart-live-region-text',
        section:
          'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    /*
     * Main cart footer
     */
    if (
      mainCartFooter &&
      mainCartFooter.dataset.id
    ) {
      sections.push({
        id: 'main-cart-footer',
        section:
          mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    /*
     * IMPORTANT:
     *
     * cart-icon-bubble has intentionally
     * been removed.
     *
     * Utenzo uses:
     * .ch-cart-count
     *
     * instead.
     */

    return sections;
  }


  /* ==========================================================
     UPDATE QUANTITY / REMOVE PRODUCT
     ========================================================== */

  updateQuantity(
    line,
    quantity,
    event,
    name,
    variantId
  ) {
    this.enableLoading(line);

    const sections =
      this.getSectionsToRender();

    const body = JSON.stringify({
      line,
      quantity,

      sections: sections.map(
        (section) => section.section
      ),

      sections_url:
        window.location.pathname,
    });

    const eventTarget =
      event.currentTarget instanceof
      CartRemoveButton
        ? 'clear'
        : 'change';

    fetch(
      `${routes.cart_change_url}`,
      {
        ...fetchConfig(),
        body,
      }
    )

      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Cart change failed: ${response.status}`
          );
        }

        return response.text();
      })

      .then((state) => {
        const parsedState =
          JSON.parse(state);

        CartPerformance.measure(
          `${eventTarget}:paint-updated-sections`,
          () => {

            const quantityElement =
              document.getElementById(
                `Quantity-${line}`
              ) ||
              document.getElementById(
                `Drawer-quantity-${line}`
              );

            const items =
              document.querySelectorAll(
                '.cart-item'
              );


            /* ===============================================
               SHOPIFY CART ERROR
               =============================================== */

            if (parsedState.errors) {
              if (quantityElement) {
                quantityElement.value =
                  quantityElement.getAttribute(
                    'value'
                  );
              }

              this.updateLiveRegions(
                line,
                parsedState.errors
              );

              return;
            }


            /* ===============================================
               EMPTY STATE
               =============================================== */

            this.classList.toggle(
              'is-empty',
              parsedState.item_count === 0
            );

            const cartDrawerWrapper =
              document.querySelector(
                'cart-drawer'
              );

            const cartFooter =
              document.getElementById(
                'main-cart-footer'
              );

            if (cartFooter) {
              cartFooter.classList.toggle(
                'is-empty',
                parsedState.item_count === 0
              );
            }

            if (cartDrawerWrapper) {
              cartDrawerWrapper.classList.toggle(
                'is-empty',
                parsedState.item_count === 0
              );
            }


            /* ===============================================
               UTENZO HEADER CART COUNT
               =============================================== */

            updateUtenzoCartCount(
              parsedState.item_count
            );


            /* ===============================================
               RENDER RETURNED SHOPIFY SECTIONS
               =============================================== */

            sections.forEach((section) => {
              const sectionContainer =
                document.getElementById(
                  section.id
                );

              if (!sectionContainer) {
                return;
              }

              const returnedSection =
                parsedState.sections &&
                parsedState.sections[
                  section.section
                ];

              if (!returnedSection) {
                return;
              }

              const newHTML =
                this.getSectionInnerHTML(
                  returnedSection,
                  section.selector
                );

              if (newHTML === null) {
                return;
              }

              const elementToReplace =
                sectionContainer.querySelector(
                  section.selector
                ) ||
                sectionContainer;

              if (elementToReplace) {
                elementToReplace.innerHTML =
                  newHTML;
              }
            });


            /* ===============================================
               QUANTITY RESULT
               =============================================== */

            const updatedValue =
              parsedState.items[line - 1]
                ? parsedState.items[
                    line - 1
                  ].quantity
                : undefined;

            let message = '';

            if (
              quantityElement &&
              items.length ===
                parsedState.items.length &&
              updatedValue !==
                parseInt(
                  quantityElement.value,
                  10
                )
            ) {
              if (
                typeof updatedValue ===
                'undefined'
              ) {
                message =
                  window.cartStrings.error;
              } else {
                message =
                  window.cartStrings.quantityError.replace(
                    '[quantity]',
                    updatedValue
                  );
              }
            }

            this.updateLiveRegions(
              line,
              message
            );


            /* ===============================================
               RESTORE FOCUS
               =============================================== */

            const lineItem =
              document.getElementById(
                `CartItem-${line}`
              ) ||
              document.getElementById(
                `CartDrawer-Item-${line}`
              );

            const focusTarget =
              lineItem &&
              name
                ? lineItem.querySelector(
                    `[name="${name}"]`
                  )
                : null;

            if (
              lineItem &&
              focusTarget
            ) {
              if (cartDrawerWrapper) {
                trapFocus(
                  cartDrawerWrapper,
                  focusTarget
                );
              } else {
                focusTarget.focus();
              }
            } else if (
              parsedState.item_count === 0 &&
              cartDrawerWrapper
            ) {
              const emptyDrawer =
                cartDrawerWrapper.querySelector(
                  '.drawer__inner-empty'
                );

              const emptyDrawerLink =
                cartDrawerWrapper.querySelector(
                  'a'
                );

              if (
                emptyDrawer &&
                emptyDrawerLink
              ) {
                trapFocus(
                  emptyDrawer,
                  emptyDrawerLink
                );
              }
            } else if (
              document.querySelector(
                '.cart-item'
              ) &&
              cartDrawerWrapper
            ) {
              const cartItemName =
                document.querySelector(
                  '.cart-item__name'
                );

              if (cartItemName) {
                trapFocus(
                  cartDrawerWrapper,
                  cartItemName
                );
              }
            }
          }
        );


        /* ===============================================
           PERFORMANCE
           =============================================== */

        CartPerformance.measureFromEvent(
          `${eventTarget}:user-action`,
          event
        );


        /* ===============================================
           PUBLISH CART UPDATE
           =============================================== */

        publish(
          PUB_SUB_EVENTS.cartUpdate,
          {
            source: 'cart-items',
            cartData: parsedState,
            variantId,
          }
        );
      })

      .catch((error) => {
        console.error(
          'Cart quantity update error:',
          error
        );

        this.querySelectorAll(
          '.loading__spinner'
        ).forEach((overlay) => {
          overlay.classList.add('hidden');
        });

        const errors =
          document.getElementById(
            'cart-errors'
          ) ||
          document.getElementById(
            'CartDrawer-CartErrors'
          );

        if (errors) {
          errors.textContent =
            window.cartStrings.error;
        }
      })

      .finally(() => {
        this.disableLoading(line);
      });
  }


  /* ==========================================================
     LIVE REGIONS
     ========================================================== */

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(
        `Line-item-error-${line}`
      ) ||
      document.getElementById(
        `CartDrawer-LineItemError-${line}`
      );

    if (lineItemError) {
      const errorText =
        lineItemError.querySelector(
          '.cart-item__error-text'
        );

      if (errorText) {
        errorText.textContent =
          message || '';
      }
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute(
        'aria-hidden',
        true
      );
    }

    const cartStatus =
      document.getElementById(
        'cart-live-region-text'
      ) ||
      document.getElementById(
        'CartDrawer-LiveRegionText'
      );

    if (!cartStatus) return;

    cartStatus.setAttribute(
      'aria-hidden',
      false
    );

    setTimeout(() => {
      cartStatus.setAttribute(
        'aria-hidden',
        true
      );
    }, 1000);
  }


  /* ==========================================================
     SECTION HTML
     ========================================================== */

  getSectionInnerHTML(
    html,
    selector
  ) {
    if (!html) return null;

    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsedHTML.querySelector(selector);

    return element
      ? element.innerHTML
      : null;
  }


  /* ==========================================================
     LOADING STATE
     ========================================================== */

  enableLoading(line) {
    const mainCartItems =
      document.getElementById(
        'main-cart-items'
      ) ||
      document.getElementById(
        'CartDrawer-CartItems'
      );

    if (mainCartItems) {
      mainCartItems.classList.add(
        'cart__items--disabled'
      );
    }

    const cartItemElements =
      this.querySelectorAll(
        `#CartItem-${line} .loading__spinner`
      );

    const cartDrawerItemElements =
      this.querySelectorAll(
        `#CartDrawer-Item-${line} .loading__spinner`
      );

    [
      ...cartItemElements,
      ...cartDrawerItemElements,
    ].forEach((overlay) => {
      overlay.classList.remove('hidden');
    });

    if (
      document.activeElement &&
      typeof document.activeElement.blur ===
        'function'
    ) {
      document.activeElement.blur();
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute(
        'aria-hidden',
        false
      );
    }
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById(
        'main-cart-items'
      ) ||
      document.getElementById(
        'CartDrawer-CartItems'
      );

    if (mainCartItems) {
      mainCartItems.classList.remove(
        'cart__items--disabled'
      );
    }

    const cartItemElements =
      this.querySelectorAll(
        `#CartItem-${line} .loading__spinner`
      );

    const cartDrawerItemElements =
      this.querySelectorAll(
        `#CartDrawer-Item-${line} .loading__spinner`
      );

    [
      ...cartItemElements,
      ...cartDrawerItemElements,
    ].forEach((overlay) => {
      overlay.classList.add('hidden');
    });
  }
}


/* ============================================================
   REGISTER CART ITEMS
   ============================================================ */

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    CartItems
  );
}


/* ============================================================
   CART NOTE
   ============================================================ */

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',

    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',

          debounce(
            (event) => {
              const body =
                JSON.stringify({
                  note: event.target.value,
                });

              fetch(
                `${routes.cart_update_url}`,
                {
                  ...fetchConfig(),
                  body,
                }
              )
                .then((response) => {
                  if (!response.ok) {
                    throw new Error(
                      `Cart note update failed: ${response.status}`
                    );
                  }

                  return response;
                })

                .then(() => {
                  CartPerformance.measureFromEvent(
                    'note-update:user-action',
                    event
                  );
                })

                .catch((error) => {
                  console.error(
                    'Cart note update error:',
                    error
                  );
                });
            },

            ON_CHANGE_DEBOUNCE_TIMER
          )
        );
      }
    }
  );
}